// Energy Hub palette + tank band math (Energy HUB part 1, Slice D).
//
// The five PILLAR colours are LOCKED by the PO — they are the visual identity of
// the Pizza Chart and must NEVER be theme-varied (no dark-mode variant, no
// opacity trick, no "close enough" substitute). Contrast in dark mode is bought
// with a separator STROKE drawn around each slice in the panel's own surface
// colour, never by shifting a fill.
//
// `reasoning` is NORMAL RED (#DC2626) since #385, which replaced the original
// light red (#FCA5A5) — the value is an acceptance criterion, not a taste call.
// Why this red: a pure hue-0 red (Tailwind red-600) that clears the 3:1
// non-text contrast floor on the dark glass (4.0:1 on #0A0E13, 3.6:1 on a
// raised surface), stays visibly apart from the design system's ERROR red
// (`pai-red` #F04438 — lighter and orange-shifted, and in this panel used only
// for error TEXT, never a fill) and from the tank's critical band (#B91C1C,
// darker). Every surface reads it from here: the pizza, its legend, the daily
// chart's day view.

export const PILLAR_COLORS = {
    listening: '#1E3A8A',  // Navy
    viewing: '#38BDF8',    // Light/Sky Blue
    writing: '#374151',    // Graphite
    reasoning: '#DC2626',  // NORMAL RED (#385) — locked acceptance criterion
    executing: '#166534',  // Forest Green
};

// Canonical render order. The BE always returns all five pillars (zeros
// included) but the FE never relies on the response's ordering.
export const PILLAR_ORDER = ['listening', 'viewing', 'writing', 'reasoning', 'executing'];

// The action bar's health bands. Distinct from the pillar palette: these express
// "how much is left", not "what it was spent on".
export const ENERGY_BANDS = {
    green: '#16A34A',
    yellow: '#EAB308',
    orange: '#F97316',
    red: '#B91C1C',
    empty: '#B91C1C',
};

// The gray stack (storage footprint) is deliberately achromatic in both themes —
// it is not a pillar and must never read as one.
export const GRAY_STACK_COLOR = '#9CA3AF';

/**
 * Every `*_pts` value on the wire is a STRING with 2 decimals ("24000.00").
 * Strings are what we DISPLAY (verbatim — never re-formatted through
 * parseFloat/toFixed, which would silently drop the BE's rounding contract);
 * this converter exists solely for geometry (bar widths, arc angles).
 */
export const toPoints = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

/**
 * Remaining action energy as a percentage of what the cycle opened with.
 * Divide-by-zero (no opening balance, or a missing/absent tank) is treated as
 * 0 — an unknown tank must never render as "full".
 */
export function tankPercent(tank) {
    const opening = toPoints(tank?.cycle_opening_action_pts);
    if (opening <= 0) return 0;
    return (toPoints(tank?.action_pts) / opening) * 100;
}

/**
 * Band for a remaining-percentage. Boundaries are INCLUSIVE at the top of the
 * lower band (60.0 → yellow, 40.0 → orange, 15.0 → red, 0 → empty), so a value
 * sitting exactly on a threshold always reports the more urgent state.
 */
export function energyBand(pct) {
    const p = Number.isFinite(pct) ? pct : 0;
    if (p <= 0) return { state: 'empty', color: ENERGY_BANDS.empty };
    if (p <= 15) return { state: 'red', color: ENERGY_BANDS.red };
    if (p <= 40) return { state: 'orange', color: ENERGY_BANDS.orange };
    if (p <= 60) return { state: 'yellow', color: ENERGY_BANDS.yellow };
    return { state: 'green', color: ENERGY_BANDS.green };
}
